import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Check, Route, Users, RotateCcw, Clock, Target } from 'lucide-react';

const RoutingStrategyModal = ({ isOpen, onClose, onSelect, selectedValue }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const strategies = [
    {
      value: 'skill-based',
      label: 'Skill Based',
      description: 'Route tickets based on agent skills and expertise',
      icon: Target,
      features: ['Matches agent skills to ticket requirements', 'Considers agent availability', 'Prioritizes expertise areas']
    },
    {
      value: 'workload-balanced',
      label: 'Workload Balanced',
      description: 'Distribute tickets evenly across available agents',
      icon: Users,
      features: ['Equal distribution of workload', 'Considers current ticket count', 'Prevents agent overload']
    },
    {
      value: 'round-robin',
      label: 'Round Robin',
      description: 'Assign tickets in a circular order to agents',
      icon: RotateCcw,
      features: ['Simple rotation system', 'Fair distribution', 'Predictable assignment pattern']
    },
    {
      value: 'least-busy',
      label: 'Least Busy',
      description: 'Assign tickets to agents with the least current workload',
      icon: Clock,
      features: ['Assigns to least busy agents', 'Real-time workload monitoring', 'Optimizes response times']
    }
  ];

  const filteredStrategies = strategies.filter(strategy =>
    strategy.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    strategy.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (value) => {
    onSelect(value);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Routing Strategy"
      size="lg"
    >
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search routing strategies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Strategy Options */}
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredStrategies.map((strategy) => {
            const IconComponent = strategy.icon;
            const isSelected = selectedValue === strategy.value;
            
            return (
              <div
                key={strategy.value}
                onClick={() => handleSelect(strategy.value)}
                className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <IconComponent className={`w-5 h-5 ${
                      isSelected ? 'text-blue-600' : 'text-gray-600'
                    }`} />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{strategy.label}</h3>
                      {isSelected && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{strategy.description}</p>
                    
                    <div className="space-y-1">
                      {strategy.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-gray-500">
                          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredStrategies.length === 0 && (
          <div className="text-center py-8">
            <Route className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No routing strategies found</p>
            <p className="text-sm text-gray-500">Try adjusting your search terms</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RoutingStrategyModal;
